import axios from 'axios';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  if (!base64String) return null;
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return typeof window !== 'undefined' && 
         'serviceWorker' in navigator && 
         'PushManager' in window &&
         'Notification' in window;
}

// Check if Safari browser
export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

// Check current permission status
export function getPermissionStatus() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
}

// Request notification permission
export async function requestPermission() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker not supported');
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

// Subscribe to push notifications
export async function subscribeToPush() {
  try {
    // Register service worker first
    const registration = await registerServiceWorker();
    
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    
    // Request permission if not granted
    const permission = await requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    
    console.log('Raw subscription:', subscription);
    console.log('Endpoint:', subscription.endpoint);
    
    // Validate subscription data
    if (!subscription.endpoint) {
      throw new Error('Push subscription failed: empty endpoint');
    }
    
    const p256dh = subscription.getKey('p256dh');
    const auth = subscription.getKey('auth');
    
    console.log('p256dh:', p256dh);
    console.log('auth:', auth);
    
    if (!p256dh || !auth) {
      throw new Error('Push subscription failed: missing keys');
    }
    
    // Send subscription to backend
    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(p256dh),
        auth: arrayBufferToBase64(auth)
      }
    };
    
    console.log('Sending subscription data:', subscriptionData);
    
    await axios.post('/notifications/subscribe', subscriptionData);
    
    console.log('Push subscription successful');
    return true;
  } catch (error) {
    console.error('Push subscription failed:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
    }
    
    await axios.delete('/notifications/unsubscribe');
    
    console.log('Push unsubscription successful');
    return true;
  } catch (error) {
    console.error('Push unsubscription failed:', error);
    throw error;
  }
}

// Check if already subscribed
export async function isSubscribedToPush() {
  try {
    if (!isPushSupported()) return false;
    
    // Check if service worker is registered
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) return false;
    
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

// Send test notification
export async function sendTestNotification() {
  try {
    const response = await axios.post('/notifications/test');
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Helper function to convert ArrayBuffer to base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// Show browser notification (for in-app notifications when browser is focused)
export function showBrowserNotification(title, body, data = {}) {
  if (getPermissionStatus() !== 'granted') return;
  
  const notification = new Notification(title, {
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    data,
    requireInteraction: false
  });
  
  notification.onclick = () => {
    window.focus();
    if (data.url) {
      window.location.href = data.url;
    }
    notification.close();
  };
  
  // Auto close after 5 seconds
  setTimeout(() => notification.close(), 5000);
}
